import { useMemo, useCallback } from 'react';
import { useSigma, useCamera } from '@react-sigma/core';
import { useGraphStore } from '@/stores/graph';
import {
  X,
  ChevronLeft,
  Expand,
  Loader2,
} from 'lucide-react';
import { clsx } from 'clsx';
import { ENTITY_CLASS_COLORS, ENTITY_CLASS_LABELS, EDGE_TYPE_COLORS } from './graphAdapter';
import type { NodeAttributes, EdgeAttributes } from './graphAdapter';

interface NeighborInfo {
  nodeId: string;
  label: string;
  entityClass: string;
  edgeType: string;
  linkSource: string;
  similarity: number | null;
}

export function NodeDetailPanel() {
  const sigma = useSigma<NodeAttributes, EdgeAttributes>();
  const { gotoNode } = useCamera();
  const {
    selectedNodeId,
    selectNode,
    navigateToNode,
    goBack,
    navigationHistory,
    expandNeighborhood,
    vizLoading,
  } = useGraphStore();

  const graph = sigma.getGraph();

  const nodeAttrs = useMemo(() => {
    if (!selectedNodeId || !graph.hasNode(selectedNodeId)) return null;
    return graph.getNodeAttributes(selectedNodeId);
  }, [selectedNodeId, graph]);

  const neighbors = useMemo<NeighborInfo[]>(() => {
    if (!selectedNodeId || !graph.hasNode(selectedNodeId)) return [];

    const result: NeighborInfo[] = [];
    graph.forEachEdge(selectedNodeId, (edge, attrs, source, target) => {
      const neighborId = source === selectedNodeId ? target : source;
      if (!graph.hasNode(neighborId)) return;
      const neighborAttrs = graph.getNodeAttributes(neighborId);
      result.push({
        nodeId: neighborId,
        label: neighborAttrs.label,
        entityClass: neighborAttrs.entityClass,
        edgeType: attrs.edgeType,
        linkSource: attrs.linkSource,
        similarity: attrs.similarity,
      });
    });

    result.sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0));
    return result;
  }, [selectedNodeId, graph]);

  const handleNavigate = useCallback(
    (nodeId: string) => {
      navigateToNode(nodeId);
      if (graph.hasNode(nodeId)) {
        gotoNode(nodeId, { duration: 400 });
      }
    },
    [navigateToNode, gotoNode, graph],
  );

  const handleExpand = useCallback(() => {
    if (selectedNodeId) {
      expandNeighborhood(selectedNodeId, 1);
    }
  }, [selectedNodeId, expandNeighborhood]);

  if (!selectedNodeId || !nodeAttrs) return null;

  const color = ENTITY_CLASS_COLORS[nodeAttrs.entityClass] ?? '#94a3b8';
  const classLabel =
    ENTITY_CLASS_LABELS[nodeAttrs.entityClass] ??
    nodeAttrs.entityClass.replace(/_/g, ' ');

  const edgeGroups = new Map<string, NeighborInfo[]>();
  for (const n of neighbors) {
    const key = n.linkSource === 'structural' ? 'structural' : n.edgeType;
    const list = edgeGroups.get(key) ?? [];
    list.push(n);
    edgeGroups.set(key, list);
  }

  return (
    <div className="absolute right-0 top-0 bottom-0 w-80 bg-slate-900 border-l border-slate-700/50 flex flex-col overflow-hidden z-30">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-start gap-2">
          {navigationHistory.length > 0 && (
            <button
              onClick={goBack}
              className="p-1 text-slate-500 hover:text-white rounded transition-colors flex-shrink-0 mt-0.5"
              title="Go back"
            >
              <ChevronLeft size={14} />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: color }}
              />
              <h2 className="text-sm font-semibold text-white truncate">
                {nodeAttrs.label}
              </h2>
            </div>
            <p className="text-[10px] text-slate-500 capitalize">{classLabel}</p>
          </div>
          <button
            onClick={() => selectNode(null)}
            className="p-1 text-slate-500 hover:text-white rounded transition-colors flex-shrink-0"
          >
            <X size={14} />
          </button>
        </div>

        {navigationHistory.length > 0 && (
          <div className="mt-2 flex items-center gap-1 overflow-x-auto">
            {navigationHistory.slice(-3).map((id, i) => {
              const label = graph.hasNode(id)
                ? graph.getNodeAttribute(id, 'label')
                : id.slice(0, 8);
              return (
                <button
                  key={`${id}-${i}`}
                  onClick={() => handleNavigate(id)}
                  className="text-[9px] text-slate-500 hover:text-teal-400 bg-slate-800 rounded px-1.5 py-0.5 truncate max-w-[80px] transition-colors"
                  title={label}
                >
                  {label}
                </button>
              );
            })}
            <span className="text-[9px] text-slate-600 mx-0.5">&rarr;</span>
            <span className="text-[9px] text-teal-400 font-medium truncate max-w-[80px]">
              {nodeAttrs.label}
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* Metrics */}
        <section>
          <SectionTitle>Metrics</SectionTitle>
          <div className="grid grid-cols-2 gap-2">
            <MetricCard
              label="Confidence"
              value={`${(nodeAttrs.confidence * 100).toFixed(0)}%`}
              color={
                nodeAttrs.confidence >= 0.7
                  ? '#22c55e'
                  : nodeAttrs.confidence >= 0.4
                    ? '#f59e0b'
                    : '#ef4444'
              }
            />
            <MetricCard
              label="Completeness"
              value={`${(nodeAttrs.completeness * 100).toFixed(0)}%`}
              color={
                nodeAttrs.completeness >= 0.7
                  ? '#22c55e'
                  : nodeAttrs.completeness >= 0.4
                    ? '#f59e0b'
                    : '#ef4444'
              }
            />
            <MetricCard label="Members" value={nodeAttrs.memberCount.toString()} />
            <MetricCard label="Connections" value={neighbors.length.toString()} />
          </div>
        </section>

        {/* Resolution type */}
        {nodeAttrs.resolutionType && (
          <section>
            <SectionTitle>Resolution</SectionTitle>
            <div className="bg-slate-800 rounded-md px-3 py-2">
              <span className="text-xs text-slate-300 capitalize">
                {nodeAttrs.resolutionType.replace(/_/g, ' ')}
              </span>
            </div>
          </section>
        )}

        {/* Expand neighborhood */}
        <button
          onClick={handleExpand}
          disabled={vizLoading}
          className="w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300 text-xs font-medium px-3 py-2 rounded-md transition-colors border border-slate-700/50"
        >
          {vizLoading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Expand size={14} />
          )}
          Expand Neighborhood
        </button>

        {/* Neighbors grouped by edge type */}
        {neighbors.length > 0 && (
          <section>
            <SectionTitle>
              Connected Nodes ({neighbors.length})
            </SectionTitle>
            <div className="space-y-3">
              {Array.from(edgeGroups.entries()).map(([groupKey, items]) => {
                const groupColor = EDGE_TYPE_COLORS[groupKey] ?? '#64748b';
                return (
                  <div key={groupKey}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <div
                        className="w-3 h-0.5 rounded-full"
                        style={{ backgroundColor: groupColor }}
                      />
                      <span className="text-[9px] uppercase font-bold text-slate-500 tracking-wider">
                        {groupKey.replace(/_/g, ' ')} ({items.length})
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      {items.slice(0, 20).map((neighbor) => {
                        const nColor = ENTITY_CLASS_COLORS[neighbor.entityClass] ?? '#94a3b8';
                        return (
                          <button
                            key={neighbor.nodeId}
                            onClick={() => handleNavigate(neighbor.nodeId)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 bg-slate-800/50 hover:bg-slate-700/80 rounded-md transition-colors text-left group"
                          >
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: nColor }}
                            />
                            <span className="flex-1 text-xs text-slate-300 group-hover:text-white truncate">
                              {neighbor.label}
                            </span>
                            {neighbor.similarity != null && (
                              <span className="text-[9px] font-mono text-slate-500">
                                {neighbor.similarity.toFixed(2)}
                              </span>
                            )}
                          </button>
                        );
                      })}
                      {items.length > 20 && (
                        <p className="text-[9px] text-slate-600 px-2 py-1">
                          +{items.length - 20} more
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ID */}
        <section>
          <SectionTitle>ID</SectionTitle>
          <code className="block bg-slate-800 rounded-md px-3 py-2 text-[10px] text-slate-400 font-mono break-all select-all">
            {selectedNodeId}
          </code>
        </section>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
      {children}
    </h3>
  );
}

function MetricCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div className="bg-slate-800 rounded-md px-2.5 py-1.5 text-center">
      <div className="text-sm font-semibold" style={{ color: color ?? '#e2e8f0' }}>
        {value}
      </div>
      <div className="text-[9px] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
